import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Link } from "react-router-dom";
import logo from "../assets/logo.PNG";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,300&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
`;

/* ───────────────────────── Shell ───────────────────────── */

const Page = styled.div`
  background: #fff;
  min-height: 100vh;
  font-family: "Mendl", sans-serif;
  color: #0a0010;
`;

const Container = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 clamp(1.25rem, 5vw, 3.5rem);
`;

/* ───────────────────────── Hero ───────────────────────── */

const Hero = styled.section`
  padding: clamp(6rem, 12vw, 8.5rem) 0 clamp(2.5rem, 5vw, 3.5rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.12);
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.6rem;

  &::before {
    content: "";
    width: 32px;
    height: 1px;
    background: #a855f7;
    display: inline-block;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.4rem, 5.5vw, 4.2rem);
  font-weight: 600;
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 1.2rem;
`;

const HeroMeta = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  font-weight: 400;
  color: rgba(10, 0, 16, 0.5);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.5rem;
`;

const HeroLede = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.8;
  color: rgba(10, 0, 16, 0.65);
  max-width: 640px;
  margin-top: 1.6rem;
`;

const AcceptBanner = styled.div`
  margin-top: 1.8rem;
  padding: 1.1rem 1.4rem;
  border-radius: 12px;
  background: linear-gradient(
    135deg,
    rgba(232, 121, 249, 0.08),
    rgba(124, 58, 237, 0.08)
  );
  border: 1px solid rgba(168, 85, 247, 0.18);
  font-family: "DM Sans", sans-serif;
  font-size: 0.82rem;
  font-weight: 500;
  line-height: 1.7;
  color: #4c1d75;
  max-width: 760px;
`;

/* ───────────────────────── Layout ───────────────────────── */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: clamp(2rem, 4vw, 5rem);
  padding: clamp(3rem, 6vw, 4.5rem) 0 6rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ── TOC ── */

const TocWrap = styled.nav`
  position: sticky;
  top: clamp(1.5rem, 4vh, 2.5rem);
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(168, 85, 247, 0.25);
    border-radius: 4px;
  }

  @media (max-width: 900px) {
    position: static;
    max-height: none;
    margin-bottom: 1rem;
  }
`;

const TocTitle = styled.div`
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(10, 0, 16, 0.35);
  margin-bottom: 1rem;
`;

const TocList = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const TocLink = styled.a`
  font-family: "DM Sans", sans-serif;
  font-size: 0.83rem;
  font-weight: 400;
  color: ${({ $active }) => ($active ? "#7c3aed" : "rgba(10, 0, 16, 0.55)")};
  text-decoration: none;
  display: block;
  line-height: 1.4;
  border-left: 2px solid
    ${({ $active }) => ($active ? "#a855f7" : "transparent")};
  padding-left: 0.75rem;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    color: #7c3aed;
  }
`;

/* ── Content ── */

const Content = styled.div`
  max-width: 760px;
`;

const Section = styled.section`
  scroll-margin-top: 2rem;
  padding-bottom: clamp(2.2rem, 4vw, 3rem);
  margin-bottom: clamp(2.2rem, 4vw, 3rem);
  border-bottom: 1px solid rgba(168, 85, 247, 0.1);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.3rem, 2.2vw, 1.6rem);
  font-weight: 600;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: baseline;
  gap: 0.6rem;

  span {
    font-size: 0.75rem;
    font-weight: 500;
    color: #a855f7;
    letter-spacing: 0.05em;
  }
`;

const SubTitle = styled.h3`
  font-size: 1.02rem;
  font-weight: 600;
  margin: 1.6rem 0 0.7rem;
  color: #1a0b2e;
`;

const P = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.85;
  color: rgba(10, 0, 16, 0.68);
  margin-bottom: 0.9rem;

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const List = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin: 0.6rem 0 1rem;
`;

const ListItem = styled.li`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  font-weight: 300;
  line-height: 1.75;
  color: rgba(10, 0, 16, 0.68);
  padding-left: 1.2rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #a855f7;
  }

  strong {
    font-weight: 600;
    color: #0a0010;
  }
`;

const Callout = styled.div`
  background: rgba(168, 85, 247, 0.05);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 10px;
  padding: 1rem 1.2rem;
  margin: 0.9rem 0;

  p {
    margin-bottom: 0;
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.8rem 0 1rem;
`;

const ContactRow = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.94rem;
  color: rgba(10, 0, 16, 0.68);

  b {
    color: #0a0010;
    font-weight: 600;
  }

  a {
    color: #7c3aed;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackHome = styled(Link)`
  font-family: "DM Sans", sans-serif;
  font-size: 0.85rem;
  color: #7c3aed;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 2rem;

  &:hover {
    text-decoration: underline;
  }
`;

/* ───────────────────────── Data ───────────────────────── */

const TOC = [
  { id: "acceptance", label: "1. Acceptance & Eligibility" },
  { id: "registration", label: "2. Registration & Verification" },
  { id: "billing", label: "3. Subscriptions & Billing" },
  { id: "conduct", label: "4. Community Standards" },
  { id: "content", label: "5. User Content & License" },
  { id: "messaging", label: "6. Messaging & Safety Features" },
  { id: "location", label: "7. Location Data" },
  { id: "safety", label: "8. Safety Disclaimer" },
  { id: "privacy", label: "9. Privacy" },
  { id: "ip", label: "10. Intellectual Property" },
  { id: "termination", label: "11. Term & Termination" },
  { id: "warranties", label: "12. Disclaimers of Warranties" },
  { id: "liability", label: "13. Limitation of Liability" },
  { id: "indemnification", label: "14. Indemnification" },
  { id: "changes", label: "15. Changes to These Terms" },
  { id: "disputes", label: "16. Governing Law & Arbitration" },
  { id: "thirdparty", label: "17. Third-Party Services" },
  { id: "general", label: "18. General Provisions" },
  { id: "compliance", label: "19. Compliance with Laws" },
  { id: "contact", label: "20. Contact Information" },
];

export default function TermsPage() {
  const [active, setActive] = useState(TOC[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Legal</Eyebrow>
          <HeroTitle>Terms of Service</HeroTitle>
          <HeroMeta>
            <span>Effective Date: July 9, 2026</span>
            <span>Version: v1.0</span>
          </HeroMeta>
          <HeroLede>
            Operated by NUDR INC, a company incorporated under the laws of the
            State of Delaware, U.S.A., with its registered office at 1105 N
            Buena Vista Street, Burbank, CA 91505 ("NudR," "Company," "we,"
            "us," or "our"). These Terms, together with the NudR Privacy Policy
            and Community Guidelines, are a legally binding agreement governing
            your access to and use of the NudR mobile applications, website,
            APIs, and related services (the "Service").
          </HeroLede>
          <AcceptBanner>
            By creating an account, tapping "I Agree," or otherwise accessing or
            using the Service, you acknowledge that you have read, understood,
            and agree to be bound by these Terms, including the binding
            arbitration and class action waiver provisions in Section 16. If you
            do not agree to these Terms, you must not access or use the Service.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            {/* 1. Acceptance */}
            <Section id="acceptance">
              <SectionTitle>
                <span>01</span>Acceptance of Terms and Eligibility
              </SectionTitle>

              <SubTitle>1.1 Age and Capacity</SubTitle>
              <P>To create an account or use the Service, you must:</P>
              <List>
                <ListItem>
                  Be at least <strong>18 years of age</strong>. NudR verifies
                  date of birth at signup and does not knowingly permit anyone
                  under 18 to register an account.
                </ListItem>
                <ListItem>
                  Have the legal capacity to enter into a binding contract under
                  the laws of your jurisdiction of residence.
                </ListItem>
                <ListItem>
                  Not be barred from using dating, social-networking, or similar
                  services under any applicable law, court order, or regulation.
                </ListItem>
                <ListItem>
                  Not be required to register, or currently be registered, as a
                  sex offender under the laws of any jurisdiction.
                </ListItem>
                <ListItem>
                  Not have been previously banned or removed from the Service
                  for violation of these Terms or the Community Guidelines,
                  unless reinstated in writing by NudR.
                </ListItem>
              </List>
              <P>
                By registering, you represent, warrant, and covenant on an
                ongoing basis that the foregoing statements are true.{" "}
                <strong>
                  Misrepresenting your age, identity, or eligibility is a
                  material breach of these Terms
                </strong>{" "}
                and may result in immediate account termination, forfeiture of
                any paid subscription or in-app purchase without refund, and,
                where required by law, referral to law enforcement.
              </P>

              <SubTitle>1.2 Minors</SubTitle>
              <P>
                The Service is not directed to, and must not be used by, anyone
                under 18. If NudR becomes aware that it has collected personal
                information from a person under 18, it will take reasonable
                steps to delete that information and terminate the associated
                account in accordance with the Privacy Policy.
              </P>
            </Section>

            {/* 2. Registration */}
            <Section id="registration">
              <SectionTitle>
                <span>02</span>Account Registration, Identity, and Verification
              </SectionTitle>

              <SubTitle>2.1 Registration Information</SubTitle>
              <P>
                When you register, you must provide accurate, current, and
                complete information, including your legal name, date of birth,
                a valid email address, and at least one profile photograph. Your
                legal name is used solely for identity-verification purposes
                and, once submitted, cannot be changed through the Service;
                corrections require a request to NudR support accompanied by
                supporting documentation.
              </P>

              <SubTitle>2.2 Identity Verification</SubTitle>
              <P>
                NudR operates an identity-verification program to reduce
                impersonation and fraudulent accounts. As part of this program
                you may be required to submit a government-issued identification
                photograph, which NudR encrypts prior to storage using
                industry-standard public-key cryptography and stores separately
                from your public profile. Verification status is displayed as
                verified, pending, or rejected. Accounts in pending status may
                access the Service during a grace period of forty-eight (48)
                hours from account creation; after that period, or if your
                account is marked rejected, access to the Service may be blocked
                until verification is completed or resolved. Submitting a
                falsified, stolen, or altered identity document is a material
                breach of these Terms and may be reported to law enforcement.
              </P>

              <SubTitle>2.3 Email Verification</SubTitle>
              <P>
                You must verify the email address associated with your account.
                If you do not verify your email address within the verification
                deadline disclosed to you at signup, access to the Service may
                be blocked until you complete verification. NudR uses email
                verification both to secure your account and to deliver
                important service, security, and transactional communications.
              </P>

              <SubTitle>2.4 Anti-Fraud and Anti-Bot Measures</SubTitle>
              <P>
                To protect the integrity of the Service, NudR employs automated
                fraud-prevention and bot-detection measures, including
                proof-of-work challenges, CAPTCHA-style human-verification
                challenges, device and session integrity signals used solely for
                fraud and abuse prevention, behavioral risk scoring, and
                IP-based rate limiting. You agree not to circumvent, disable, or
                interfere with these measures. NudR may throttle, challenge, or
                block requests it determines, in its reasonable discretion, to
                present a security or fraud risk.
              </P>

              <SubTitle>2.5 Account Security</SubTitle>
              <P>You are responsible for:</P>
              <List>
                <ListItem>
                  Maintaining the accuracy of your account information.
                </ListItem>
                <ListItem>
                  Keeping your login credentials, session tokens, and device
                  confidential and secure.
                </ListItem>
                <ListItem>
                  All activity that occurs under your account, whether or not
                  authorized by you, except to the extent caused by NudR's
                  failure to implement its stated security controls.
                </ListItem>
                <ListItem>
                  Promptly notifying NudR at the contact address in Section 20
                  if you suspect unauthorized access to your account.
                </ListItem>
              </List>
              <P>
                You may not create an account on behalf of another person,
                create more than one account for yourself without NudR's prior
                written consent, or transfer, sell, rent, or share your account
                with any other person.
              </P>
            </Section>

            {/* 3. Billing */}
            <Section id="billing">
              <SectionTitle>
                <span>03</span>Subscriptions, In-App Purchases, and Billing
              </SectionTitle>

              <SubTitle>3.1 Plans and Trials</SubTitle>
              <P>
                NudR may offer free features alongside paid subscription tiers
                and one-time in-app purchases (including, without limitation,
                "flares" and reward points). Certain subscription plans may
                include an introductory trial period, the length of which will
                be disclosed to you before purchase.
              </P>

              <SubTitle>3.2 Automatic Renewal</SubTitle>
              <P>
                <strong>Paid subscriptions automatically renew</strong> at the
                end of each billing period for a successive period of the same
                duration, at the then-current price, unless you cancel before
                the end of the current period. If a trial period is offered,
                your payment method will be charged automatically when the trial
                ends unless you cancel before it does. Billing and payment
                processing are handled by our third-party payment processor
                (currently Stripe); by subscribing, you also agree to that
                processor's applicable terms.
              </P>
              <P>
                All subscription purchases, renewals, and billing management
                take place on the NudR website via our payment processor;
                subscriptions are not billed through your device's app store
                account.
              </P>

              <SubTitle>3.3 Cancellation</SubTitle>
              <P>
                You may cancel your subscription at any time through your
                account settings or the billing portal on the NudR website.
                Cancellation takes effect at the end of the then-current billing
                period; you will continue to have access to paid features
                through the end of that period. NudR does not prorate mid-cycle
                cancellations.
              </P>

              <SubTitle>3.4 Payment Failure and Grace Periods</SubTitle>
              <P>
                If a renewal payment fails, your subscription will be marked
                past due and you will be given a grace period of approximately
                one (1) day to update your payment method before paid features
                are suspended. Continued or repeated payment failure may result
                in suspension of your subscription.
              </P>

              <SubTitle>3.5 Refunds</SubTitle>
              <P>
                <strong>
                  All payments to NudR are final and non-refundable,
                </strong>{" "}
                including, without limitation: subscription fees (including
                partial billing periods, unused features, or downgrades),
                one-time in-app purchases such as "flares" and reward points,
                and any amount forfeited due to account suspension or
                termination resulting from your violation of these Terms. This
                no-refund policy applies regardless of whether you have used the
                paid feature or subscription. All purchases are processed on the
                web through our payment processor. If, in the future, purchases
                are offered through a third-party app store (e.g., the Apple App
                Store or Google Play Store), such purchases will additionally be
                subject to that app store's own refund policy and procedures,
                which NudR does not control.
              </P>
              <P>
                Certain U.S. states impose specific disclosure or
                cancellation-reminder requirements on automatically renewing
                subscriptions ("auto-renewal" or "negative option" laws). Where
                such a law applies to you, NudR will provide the notices
                required by that law, and nothing in this Section limits any
                non-waivable refund or cancellation right that law provides.
                Except as required by such law, all sales are final.
              </P>
            </Section>

            {/* 4. Conduct */}
            <Section id="conduct">
              <SectionTitle>
                <span>04</span>Community Standards and Prohibited Conduct
              </SectionTitle>
              <P>You agree that you will not, and will not attempt to:</P>
              <List>
                <ListItem>
                  Post, upload, or transmit content that is pornographic or
                  sexually explicit, including any depiction of exposed
                  genitalia, sexual acts, or masturbation, whether in profile
                  photographs, public-facing content, or shared media.
                  Suggestive or non-explicit content is permitted only to the
                  extent allowed by the Community Guidelines, which define the
                  applicable content standards.
                </ListItem>
                <ListItem>
                  Harass, threaten, stalk, defame, or intimidate any user.
                </ListItem>
                <ListItem>
                  Post or transmit content that is illegal, hateful,
                  discriminatory, sexually exploitative of minors, or that
                  incites or threatens violence.
                </ListItem>
                <ListItem>
                  Impersonate any person or entity, or misrepresent your
                  identity, age, or affiliation.
                </ListItem>
                <ListItem>
                  Upload malware or engage in any form of cyberattack against
                  the Service, other users, or NudR infrastructure.
                </ListItem>
                <ListItem>
                  Scrape, harvest, crawl, or otherwise collect data from the
                  Service, including profile information, photographs, or
                  messages, other than through the Service's own interfaces for
                  personal, non-commercial use.
                </ListItem>
                <ListItem>
                  Circumvent, disable, or interfere with security-related
                  features of the Service, including verification, rate
                  limiting, encryption, or content moderation.
                </ListItem>
                <ListItem>
                  Use bots, scripts, automated tools, or fake or duplicate
                  profiles to interact with the Service.
                </ListItem>
                <ListItem>
                  Solicit money, cryptocurrency, gifts, or financial information
                  from another user, or otherwise engage in "romance scam"
                  conduct.
                </ListItem>
                <ListItem>
                  Share another person's private or personally identifying
                  information without that person's consent ("doxxing").
                </ListItem>
                <ListItem>
                  Use the Service for any commercial solicitation, advertising,
                  or promotional purpose not expressly authorized by NudR.
                </ListItem>
              </List>
              <P>
                Violation of this Section may result in warning, feature
                restriction, temporary suspension, or permanent termination of
                your account, at NudR's sole discretion, and without refund of
                any prepaid fees.
              </P>
            </Section>

            {/* 5. Content */}
            <Section id="content">
              <SectionTitle>
                <span>05</span>User Content and License Grant
              </SectionTitle>

              <SubTitle>5.1 Ownership</SubTitle>
              <P>
                You retain all ownership rights in the photographs, messages,
                and other content you submit to the Service ("User Content").
                NudR does not claim ownership of your User Content.
              </P>

              <SubTitle>5.2 License to NudR</SubTitle>
              <P>
                By submitting User Content, you grant NudR a worldwide,
                non-exclusive, royalty-free, sublicensable, and transferable
                license to host, store, cache, reproduce, resize, and display
                your User Content{" "}
                <strong>
                  solely as necessary to operate, secure, and improve the
                  Service
                </strong>{" "}
                (for example, generating compressed image variants, applying
                safety blurring to hidden photographs, and processing images for
                content-safety scanning). This license terminates as to a
                specific item of User Content when you delete it, subject to (a)
                residual copies in backups or caches that are purged in the
                ordinary course, and (b) content already shared with, or
                reported by, other users, which may be retained as described in
                the Privacy Policy for safety, moderation, and legal-compliance
                purposes.
              </P>

              <SubTitle>5.3 Your Representations</SubTitle>
              <P>By submitting User Content, you represent and warrant that:</P>
              <List>
                <ListItem>
                  You own the content or have all rights necessary to grant the
                  license in Section 5.2.
                </ListItem>
                <ListItem>
                  The content does not violate any applicable law.
                </ListItem>
                <ListItem>
                  The content does not infringe, misappropriate, or violate any
                  third party's intellectual property, privacy, or publicity
                  rights.
                </ListItem>
                <ListItem>
                  The content does not depict any person under 18 in any manner,
                  and every identifiable person depicted is 18 or older and,
                  where that person is not you, has consented to appearing in
                  the content.
                </ListItem>
              </List>

              <SubTitle>5.4 Content Moderation</SubTitle>
              <P>
                NudR uses automated content-safety scanning on uploaded images,
                including profile photographs and shared media, and, where
                flagged, human review, to detect content that violates these
                Terms or the Community Guidelines, including the prohibition on
                pornographic or sexually explicit content in Section 04. NudR
                may remove, blur, restrict the visibility of, or refuse to
                display any User Content, and may take enforcement action
                against the associated account, without prior notice.
              </P>
            </Section>

            {/* 6. Messaging */}
            <Section id="messaging">
              <SectionTitle>
                <span>06</span>Messaging, Encryption, Consent, and Safety
                Features
              </SectionTitle>

              <SubTitle>
                6.1 End-to-End Encrypted, Disappearing Messages
              </SubTitle>
              <P>
                Text messages exchanged between matched users are encrypted
                end-to-end using per-conversation cryptographic keys generated
                on your device; NudR is not able to read the content of your
                encrypted messages in the ordinary course of operating the
                Service.{" "}
                <strong>
                  Once a message has been read by its recipient, it
                  automatically expires and is permanently deleted from NudR's
                  systems within twenty-four (24) hours. If a message has not
                  been read, it is automatically and permanently deleted no
                  later than thirty (30) days after it was sent, whether or not
                  it was ever read.
                </strong>{" "}
                This applies regardless of the match, unmatch, or block status
                of the conversation (see Section 6.6).
              </P>
              <Callout>
                <P>
                  <strong>Important limitation:</strong> when you or the other
                  party to a conversation submit a report about a message (see
                  Section 6.4), the reported message content is decrypted and
                  retained beyond the retention periods described above, and
                  made available to NudR's moderation team, solely to
                  investigate that report. Message metadata (such as timestamps,
                  delivery status, and the fact that a conversation exists) is
                  not end-to-end encrypted and may be retained and processed by
                  NudR as described in the Privacy Policy.
                </P>
              </Callout>

              <SubTitle>6.2 Consent-Based, Disappearing Media Sharing</SubTitle>
              <P>
                Sending or receiving images, voice notes, video, or placing
                in-app calls with a match requires that both users mutually
                grant consent for that media type within the chat. Either user
                may revoke previously granted consent at any time; revocation
                applies prospectively and does not affect media already sent.{" "}
                <strong>
                  Like text messages, shared media (images, voice notes, and
                  video) is automatically and permanently deleted from NudR's
                  systems within twenty-four (24) hours after it has been viewed
                  by the recipient, or no later than thirty (30) days after it
                  was sent if it is never viewed,
                </strong>{" "}
                subject to the same reported-content exception described in
                Section 6.1.
              </P>

              <SubTitle>6.3 Screenshot and Screen-Capture Prevention</SubTitle>
              <P>
                NudR uses operating-system-level controls to actively restrict
                screenshot capture and screen recording while you are viewing
                shared media within a conversation. Where the underlying device
                or operating system does not allow NudR to technically block a
                capture, the Service instead{" "}
                <strong>
                  detects the capture attempt and notifies the other participant
                </strong>
                . Repeated screenshot or screen-recording activity in violation
                of the Community Guidelines is tracked on a strike basis and may
                result in account suspension. You agree not to use third-party
                tools, external devices (e.g., a second camera),
                jailbreaking/rooting, or other means to circumvent or evade
                these protections.
              </P>

              <SubTitle>6.4 Reporting and Moderation</SubTitle>
              <P>
                You may report another user, a profile, or a specific message
                for violation of these Terms or the Community Guidelines.
                Submitting a report authorizes NudR to review the reported
                content — including, for message reports, the decrypted content
                of the reported message and reasonable surrounding context — for
                the purpose of investigating and acting on the report. False or
                malicious reporting is itself a violation of these Terms.
              </P>

              <SubTitle>6.5 Unmatching and Blocking</SubTitle>
              <P>
                Either party to a match may unmatch or block the other at any
                time. Unmatching and blocking stop further messaging between the
                parties immediately; because all messages and media are
                independently subject to the read/unread deletion schedule
                described in Section 6.6, unmatching or blocking does not itself
                change when previously sent content is deleted.
              </P>

              <SubTitle>6.6 Message and Media Retention</SubTitle>
              <P>
                <strong>
                  NudR retains chat messages and shared media only for a limited
                  window, on a read-dependent schedule:
                </strong>{" "}
                content that has been read (or, for media, viewed) by its
                recipient is automatically and permanently deleted within
                twenty-four (24) hours of being read; content that is never read
                or viewed is automatically and permanently deleted no later than
                thirty (30) days after it was sent. This applies regardless of
                whether a conversation is ongoing, unmatched, or blocked. NudR
                is not able to recover deleted messages or media once the
                applicable period has elapsed, and you are solely responsible
                for saving, screenshotting (where not restricted under Section
                6.3), or otherwise retaining outside the Service any content you
                wish to keep. The sole exception is content associated with an
                active report under Section 6.4, which may be retained longer
                for investigation, safety, and legal-compliance purposes as
                described in the Privacy Policy.
              </P>
            </Section>

            {/* 7. Location */}
            <Section id="location">
              <SectionTitle>
                <span>07</span>Location Data
              </SectionTitle>
              <P>
                <strong>
                  NudR collects your device's precise, real-time GPS location
                  while the NudR app is open and in active, foreground use
                </strong>{" "}
                — i.e., "while using the app" location access, consistent with
                the location-permission setting you grant through your device's
                operating system.{" "}
                <strong>
                  Where real-time GPS is unavailable, disabled, or fails to
                  resolve, NudR falls back to an approximate location derived
                  from your internet connection (your IP address) via our
                  network provider, Cloudflare.
                </strong>{" "}
                NudR does not collect your GPS location while the app is closed
                or running in the background. Your location is used to power
                distance-based matching and is displayed to other users only as
                an approximate area (for example, a state or region name, or a
                distance range) — never as your exact coordinates. You can view,
                change, or revoke device-level location access at any time
                through your operating system settings; note that even if you
                disable GPS access, an approximate IP-based location may still
                be derived when you connect to the Service, and disabling
                location entirely will materially limit or disable the Service's
                core matching and discovery functionality.
              </P>
            </Section>

            {/* 8. Safety */}
            <Section id="safety">
              <SectionTitle>
                <span>08</span>Safety Disclaimer and Assumption of Risk
              </SectionTitle>
              <P>
                <strong>
                  NudR does not conduct criminal background checks, sex-offender
                  registry checks, or comprehensive identity investigations on
                  every user
                </strong>{" "}
                unless expressly stated for a specific feature or jurisdiction.
                Identity verification under Section 2.2 confirms that a user has
                submitted a matching government identification photograph; it
                does not confirm the user's criminal history, marital status,
                intentions, or truthfulness.
              </P>
              <P>
                You are solely responsible for exercising caution, judgment, and
                discretion when interacting with other users online and, in
                particular, before meeting any user in person. NudR strongly
                recommends that you meet in a public place, tell a friend or
                family member of your plans, and avoid sharing financial
                information with anyone you meet through the Service.
              </P>
              <P>
                <strong>
                  NudR makes no representation or warranty regarding the
                  identity, background, intentions, conduct, or truthfulness of
                  any user, and disclaims all liability for interactions between
                  users, whether occurring on or off the Service.
                </strong>
              </P>
            </Section>

            {/* 9. Privacy */}
            <Section id="privacy">
              <SectionTitle>
                <span>09</span>Privacy
              </SectionTitle>
              <P>
                Your use of the Service, and NudR's collection, use, storage,
                and disclosure of information about you — including verification
                identification documents, location signals, profile and message
                metadata, device identifiers, and payment information — is
                governed by the{" "}
                <Link to="/privacy" style={{ color: "#7c3aed" }}>
                  NudR Privacy Policy
                </Link>
                , which is incorporated into these Terms by reference. NudR
                applies technical and organizational security measures,
                including encryption of data in transit and at rest for
                sensitive categories of data, access controls, and rate
                limiting, as further described in the Privacy Policy; however,{" "}
                <strong>
                  no method of transmission or storage is completely secure
                </strong>
                , and NudR cannot guarantee absolute security.
              </P>
            </Section>

            {/* 10. IP */}
            <Section id="ip">
              <SectionTitle>
                <span>10</span>Intellectual Property
              </SectionTitle>
              <P>
                The Service, including its software, source code, APIs, user
                interfaces, visual designs, trademarks, logos, and all other
                proprietary technology, is and remains the exclusive property of
                NudR and its licensors. Except for the limited right to access
                and use the Service in accordance with these Terms, no right,
                title, or interest in the Service is transferred to you.
              </P>
              <P>
                You may not copy, modify, distribute, sell, lease, reverse
                engineer, decompile, disassemble, or otherwise attempt to derive
                the source code of any part of the Service, or create derivative
                works based on the Service, except to the extent such
                restriction is prohibited by applicable law.
              </P>
            </Section>

            {/* 11. Termination */}
            <Section id="termination">
              <SectionTitle>
                <span>11</span>Term, Suspension, and Termination
              </SectionTitle>

              <SubTitle>11.1 Term</SubTitle>
              <P>
                These Terms remain in effect for as long as you maintain an
                account or otherwise use the Service.
              </P>

              <SubTitle>11.2 Termination by NudR</SubTitle>
              <P>
                NudR may suspend or terminate your account or access to all or
                part of the Service, with or without notice, for:
              </P>
              <List>
                <ListItem>
                  Violation of these Terms or the Community Guidelines.
                </ListItem>
                <ListItem>
                  Conduct that presents a security, fraud, or safety risk to
                  NudR or its users.
                </ListItem>
                <ListItem>
                  Extended account inactivity, subject to the notice (if any)
                  described in the Privacy Policy.
                </ListItem>
                <ListItem>
                  Compliance with a legal obligation, court order, or
                  governmental request.
                </ListItem>
                <ListItem>
                  Discontinuation or material modification of the Service or a
                  specific feature.
                </ListItem>
              </List>

              <SubTitle>11.3 Termination by You</SubTitle>
              <P>
                You may terminate these Terms at any time by deleting your
                account through the app settings or by contacting NudR at the
                address in Section 20. Deletion of your account is handled in
                accordance with the data-retention provisions of the Privacy
                Policy.
              </P>

              <SubTitle>11.4 Effect of Termination</SubTitle>
              <P>
                Upon termination, your right to access the Service ends
                immediately. Sections that by their nature should survive
                termination — including Section 5.2 (as to already-shared or
                reported content), and Sections 8, 9, 10, 12, 13, 14, 16, 17,
                18, 19, and 20 — will survive. Termination does not entitle you
                to a refund of any prepaid, unused subscription fees except as
                required by applicable law.
              </P>
            </Section>

            {/* 12. Warranties */}
            <Section id="warranties">
              <SectionTitle>
                <span>12</span>Disclaimers of Warranties
              </SectionTitle>
              <P>
                <strong>
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE"
                  BASIS, WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
                  IMPLIED, OR STATUTORY.
                </strong>{" "}
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NUDR
                DISCLAIMS ALL WARRANTIES, INCLUDING IMPLIED WARRANTIES OF
                MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM COURSE OF
                DEALING OR USAGE OF TRADE. WITHOUT LIMITING THE FOREGOING, NUDR
                DOES NOT WARRANT THAT: (A) THE SERVICE WILL BE UNINTERRUPTED,
                TIMELY, SECURE, OR ERROR-FREE; (B) THE SERVICE WILL BE
                COMPATIBLE WITH YOUR DEVICE; (C) YOU WILL FORM ANY MATCH,
                RELATIONSHIP, OR SUCCESSFUL OUTCOME THROUGH THE SERVICE; OR (D)
                ANY OTHER USER'S CONDUCT, IDENTITY, OR REPRESENTATIONS ARE
                ACCURATE OR SAFE.
              </P>
              <P>
                Some U.S. states do not allow the exclusion or limitation of
                certain implied warranties, or limitations on how long an
                implied warranty lasts, so some of the exclusions and
                limitations in this Section may not apply to you. In that case,
                NudR's warranties are limited to the shortest period and the
                narrowest scope permitted by the applicable law of your state of
                residence.
              </P>
            </Section>

            {/* 13. Liability */}
            <Section id="liability">
              <SectionTitle>
                <span>13</span>Limitation of Liability
              </SectionTitle>
              <P>
                <strong>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
                  WILL NUDR, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR
                  AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                  CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS
                  OF PROFITS, REVENUE, DATA, GOODWILL, OR OTHER INTANGIBLE
                  LOSSES, ARISING OUT OF OR RELATING TO YOUR ACCESS TO OR USE
                  OF, OR INABILITY TO ACCESS OR USE, THE SERVICE, OR ANY CONDUCT
                  OR CONTENT OF ANY USER, WHETHER BASED ON WARRANTY, CONTRACT,
                  TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER LEGAL
                  THEORY, AND WHETHER OR NOT NUDR HAS BEEN ADVISED OF THE
                  POSSIBILITY OF SUCH DAMAGES.
                </strong>
              </P>
              <P>
                <strong>
                  NUDR'S AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR
                  RELATING TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE
                  GREATER OF (A) THE TOTAL AMOUNT YOU PAID TO NUDR IN THE TWELVE
                  (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE
                  CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (USD $100).
                </strong>
              </P>
              <P>
                Some U.S. states do not allow the limitation or exclusion of
                liability for incidental or consequential damages, or do not
                permit liability caps in certain circumstances (for example,
                liability for gross negligence, willful misconduct, fraud, or
                death or personal injury caused by a party's negligence, where
                applicable). In those states, and to that extent, the
                limitations in this Section apply only to the maximum extent
                permitted by applicable law, and NudR's liability is limited
                accordingly rather than excluded.
              </P>
            </Section>

            {/* 14. Indemnification */}
            <Section id="indemnification">
              <SectionTitle>
                <span>14</span>Indemnification
              </SectionTitle>
              <P>
                You agree to defend, indemnify, and hold harmless NudR and its
                officers, directors, employees, agents, and affiliates from and
                against any claims, liabilities, damages, losses, and expenses,
                including reasonable legal fees, arising out of or in any way
                connected with: (a) your access to or use of the Service; (b)
                your User Content; (c) your violation of these Terms; or (d)
                your violation of any right of a third party, including another
                user. NudR reserves the right to assume the exclusive defense
                and control of any matter otherwise subject to indemnification
                by you, in which case you agree to cooperate with NudR's defense
                of such claim.
              </P>
            </Section>

            {/* 15. Changes */}
            <Section id="changes">
              <SectionTitle>
                <span>15</span>Changes to These Terms and to the Service
              </SectionTitle>
              <P>
                NudR may revise these Terms from time to time. If a change is
                material, NudR will provide notice through the Service, by
                email, or by another reasonable method, at least the notice
                period required by applicable law before the change takes
                effect. Your continued use of the Service after a revised
                version of these Terms becomes effective constitutes your
                acceptance of the revised Terms. If you do not agree to a
                revision, your sole remedy is to stop using the Service and
                delete your account before the change takes effect.
              </P>
              <P>
                NudR may also add, modify, or discontinue any feature of the
                Service at any time, with or without notice, and will not be
                liable for any such addition, modification, or discontinuation.
              </P>
            </Section>

            {/* 16. Disputes */}
            <Section id="disputes">
              <SectionTitle>
                <span>16</span>Governing Law; Dispute Resolution; Arbitration
              </SectionTitle>

              <SubTitle>16.1 Governing Law</SubTitle>
              <P>
                These Terms, and any dispute arising out of or relating to these
                Terms or the Service, are governed by the laws of the State of
                Delaware, without regard to its conflict-of-laws principles,
                except that Section 16.3 (Binding Arbitration) is governed by
                the Federal Arbitration Act, 9 U.S.C. §§ 1 et seq. ("FAA"). This
                choice of law applies except to the extent a non-waivable
                consumer-protection law of your state of residence mandatorily
                applies.
              </P>

              <SubTitle>16.2 Informal Resolution</SubTitle>
              <P>
                Before filing a claim against NudR, you agree to first contact
                NudR at the address in Section 20 and attempt in good faith to
                resolve the dispute informally for at least thirty (30) days.
              </P>

              <SubTitle>16.3 Binding Arbitration</SubTitle>
              <Callout>
                <P>
                  <strong>
                    PLEASE READ THIS SECTION CAREFULLY — IT AFFECTS YOUR LEGAL
                    RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
                  </strong>{" "}
                  Except for disputes described in Section 16.5, you and NudR
                  agree that any dispute, claim, or controversy arising out of
                  or relating to these Terms or the Service — including its
                  formation, applicability, breach, termination, enforcement,
                  interpretation, or validity — will be resolved by binding,
                  individual arbitration administered by the American
                  Arbitration Association ("AAA") under its Consumer Arbitration
                  Rules then in effect, rather than in court, except that either
                  party may instead bring an individual claim in small-claims
                  court if it qualifies.
                </P>
              </Callout>
              <P>
                The arbitration will be conducted in English, before a single
                neutral arbitrator, and — at the election of the party who did
                not initiate the arbitration — may proceed by videoconference,
                telephone, written submissions, or in person in the county of
                your residence or another mutually agreed location. Each party
                will bear its own attorneys' fees and costs, except as the AAA
                rules or applicable law otherwise require or permit the
                arbitrator to award. The arbitrator's decision will be final and
                binding on both parties, subject only to limited judicial review
                under the FAA, and judgment on the award may be entered in any
                court of competent jurisdiction.
              </P>
              <P>
                <strong>Right to Opt Out:</strong> You may opt out of this
                arbitration agreement by sending written notice to{" "}
                <a href="mailto:legal@nudr.app" style={{ color: "#7c3aed" }}>
                  legal@nudr.app
                </a>{" "}
                within thirty (30) days of the date you first agree to these
                Terms. Your notice must include your full name, the email
                address associated with your NudR account, and a clear statement
                that you wish to opt out of arbitration. If you opt out, neither
                you nor NudR will be required to arbitrate, but every other
                provision of these Terms, including the Class Action Waiver in
                Section 16.4, will continue to apply to the fullest extent
                enforceable outside of arbitration.
              </P>

              <SubTitle>16.4 Class Action Waiver</SubTitle>
              <P>
                To the fullest extent permitted by law, you and NudR agree that
                each may bring claims against the other only in an{" "}
                <strong>
                  individual capacity, and not as a plaintiff or class member in
                  any purported class, collective, consolidated, or
                  representative proceeding
                </strong>
                . Unless both you and NudR agree otherwise in writing, an
                arbitrator may not consolidate more than one person's claims and
                may not otherwise preside over any form of a class or
                representative proceeding. If a court or arbitrator determines
                that this Class Action Waiver is unenforceable as to a
                particular claim or request for relief, that specific claim or
                request must be brought in court, and all other claims remain
                subject to arbitration under Section 16.3.
              </P>

              <SubTitle>16.5 Injunctive Relief</SubTitle>
              <P>
                Notwithstanding the foregoing, either party may seek injunctive
                or other equitable relief in a court of competent jurisdiction
                to prevent actual or threatened infringement, misappropriation,
                or violation of a party's intellectual property, security, or
                confidentiality rights.
              </P>

              <SubTitle>
                16.6 Jury Trial Waiver; Venue for Non-Arbitrable Disputes
              </SubTitle>
              <P>
                To the fullest extent permitted by applicable law, you and NudR
                each waive any right to a trial by jury in any proceeding
                arising out of or relating to these Terms or the Service. For
                any dispute that is not subject to arbitration under this
                Section 16 (including if you validly opt out under Section 16.3,
                or a court finds arbitration unenforceable as to a given claim),
                you and NudR agree that the exclusive venue will be the state or
                federal courts located in the State of Delaware, and each party
                consents to the personal jurisdiction of those courts.
              </P>
            </Section>

            {/* 17. Third-party */}
            <Section id="thirdparty">
              <SectionTitle>
                <span>17</span>Third-Party Services
              </SectionTitle>
              <P>
                The Service integrates or relies on third-party service
                providers, including payment processing, push-notification
                delivery, and cloud infrastructure providers. NudR is not
                responsible for the acts, omissions, or service interruptions of
                these third parties, and your use of any third-party service
                accessed through NudR may be subject to that provider's separate
                terms.
              </P>
            </Section>

            {/* 18. General */}
            <Section id="general">
              <SectionTitle>
                <span>18</span>General Provisions
              </SectionTitle>

              <SubTitle>18.1 Entire Agreement</SubTitle>
              <P>
                These Terms, together with the Privacy Policy and any other
                policies referenced herein, constitute the entire agreement
                between you and NudR regarding the Service and supersede all
                prior or contemporaneous agreements on that subject.
              </P>

              <SubTitle>18.2 Severability</SubTitle>
              <P>
                If any provision of these Terms is held unenforceable or
                invalid, that provision will be limited or eliminated to the
                minimum extent necessary, and the remaining provisions will
                remain in full force and effect.
              </P>

              <SubTitle>18.3 No Waiver</SubTitle>
              <P>
                NudR's failure to enforce any right or provision of these Terms
                will not be deemed a waiver of that right or provision.
              </P>

              <SubTitle>18.4 Assignment</SubTitle>
              <P>
                You may not assign or transfer these Terms, by operation of law
                or otherwise, without NudR's prior written consent. NudR may
                assign these Terms in connection with a merger, acquisition,
                corporate reorganization, or sale of substantially all of its
                assets, without restriction.
              </P>

              <SubTitle>18.5 Force Majeure</SubTitle>
              <P>
                NudR will not be liable for any failure or delay in performance
                resulting from causes beyond its reasonable control, including
                natural disasters, war, terrorism, labor disputes, internet or
                telecommunications failures, or governmental action.
              </P>

              <SubTitle>18.6 Notices</SubTitle>
              <P>
                NudR may provide notices to you via the email address associated
                with your account, through in-app notification, or by posting on
                the Service. Notices to NudR must be sent to the address in
                Section 20.
              </P>

              <SubTitle>18.7 Relationship of the Parties</SubTitle>
              <P>
                Nothing in these Terms creates any partnership, joint venture,
                employment, or agency relationship between you and NudR.
              </P>

              <SubTitle>18.8 Language</SubTitle>
              <P>
                These Terms are drafted and executed in English. If NudR
                provides a translated version of these Terms for your
                convenience, the English-language version will control in the
                event of any conflict or inconsistency between the two, to the
                extent permitted by applicable law.
              </P>
            </Section>

            {/* 19. Compliance */}
            <Section id="compliance">
              <SectionTitle>
                <span>19</span>Compliance with Laws
              </SectionTitle>
              <P>
                You agree to comply with all applicable laws in connection with
                your use of the Service, including export-control, sanctions,
                and anti-money-laundering laws. You represent that you are not
                located in, or a resident of, any country or region subject to
                comprehensive trade sanctions, and are not on any
                restricted-party list maintained by a government authority with
                jurisdiction over NudR.
              </P>
            </Section>

            {/* 20. Contact */}
            <Section id="contact">
              <SectionTitle>
                <span>20</span>Contact Information
              </SectionTitle>
              <P>
                If you have questions about these Terms, please contact us at:
              </P>
              <ContactList>
                <ContactRow>
                  <b>General / Legal:</b>{" "}
                  <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Security and Trust & Safety:</b>{" "}
                  <a href="mailto:security@nudr.io">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Registered Address:</b> 1105 N Buena Vista Street, Burbank,
                  CA 91505
                </ContactRow>
              </ContactList>
              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
